import BaseInput from '@components/v2/form/BaseInput/BaseInput';
import Radio from '@components/v2/form/Radio/Radio';
import Chip from '@components/v2/ui/Chip/Chip';
import IconButton from '@components/v2/ui/IconButton/IconButton';
import ListItemButton from '@components/v2/ui/List/ListItemButton';
import Modal from '@components/v2/ui/Modal/Modal';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box, Stack } from '@mui/material';
import { Plus } from 'lucide-react';
import LangEnum from '../../../../../../lang/LangEnum';
import { CatalogProductsTestIds } from '../../../../../../utils/testIds/CatalogProducts.testIds';
import { useTranslation } from '../../../../../translation/TranslationContext';
import { CatalogMoveToSectionTarget } from './catalogMoveToSectionModal.types';
import { useCatalogMoveToSectionModal } from './useCatalogMoveToSectionModal';

const ROW_HEIGHT_PX = 36;
const NAME_COLUMN_OFFSET_PX = 42;
const RADIO_GROUP_NAME = 'catalog-move-to-section';
const DUPLICATED_NAME_ERROR_ID = 'catalog-move-to-section-duplicated-name';

export interface CatalogMoveToSectionModalProps {
  isOpen: boolean;
  catalogId: string;
  onClose: () => void;
  onConfirm: (target: CatalogMoveToSectionTarget) => Promise<void>;
}

const CatalogMoveToSectionModal = ({
  isOpen,
  catalogId,
  onClose,
  onConfirm,
}: CatalogMoveToSectionModalProps) => {
  const { translate } = useTranslation();
  const {
    rows,
    selectedSectionId,
    draftInputValue,
    typedSectionName,
    isNameDuplicated,
    isMoving,
    isCreateDisabled,
    isConfirmDisabled,
    handleSelectSection,
    handleChangeDraftInput,
    handleCreateDraftSection,
    handleConfirm,
    handleExited,
  } = useCatalogMoveToSectionModal({ isOpen, catalogId, onConfirm });

  return (
    <Modal
      isOpen={isOpen}
      size="standard"
      testId={CatalogProductsTestIds.MOVE_TO_SECTION_MODAL}
      header={{ title: translate(LangEnum.CATALOGS_MASS_ACTIONS_MOVE_TO_SECTION) }}
      onClose={onClose}
      onExited={handleExited}
      slots={{
        headerBottom: (
          <Typography variant="body2" color="text.secondary">
            {translate(LangEnum.CATALOGS_MOVE_TO_SECTION_MODAL_TEXT)}
          </Typography>
        ),
      }}
      footer={{
        primaryAction: {
          label: LangEnum.CATALOGS_MOVE_TO_SECTION_MODAL_MOVE_LABEL,
          isDisabled: isConfirmDisabled,
          isLoading: isMoving,
          testId: CatalogProductsTestIds.MOVE_TO_SECTION_CONFIRM,
          onClick: handleConfirm,
        },
        secondaryAction: {
          label: LangEnum.GLOBAL_CANCEL_LABEL,
          onClick: onClose,
        },
      }}
    >
      <Stack role="radiogroup" sx={{ gap: 0.25 }}>
        {rows.map((row) => (
          <ListItemButton
            key={row.id}
            onClick={() => handleSelectSection(row.id)}
            sx={(theme) => ({
              gap: 1,
              minHeight: `${ROW_HEIGHT_PX}px`,
              paddingX: 0.75,
              borderRadius: `${theme.shape.borderRadiusMd}px`,
            })}
          >
            <Radio
              name={RADIO_GROUP_NAME}
              value={row.id}
              checked={selectedSectionId === row.id}
              slotProps={{ input: { 'aria-label': row.name } }}
              onChange={() => handleSelectSection(row.id)}
            />
            <Typography variant="body1" noWrap sx={{ flex: 1, minWidth: 0, paddingLeft: 0.75 }}>
              {row.name}
            </Typography>
            {row.isNew && (
              <Chip
                size="small"
                label={translate(LangEnum.CATALOGS_MOVE_TO_SECTION_MODAL_NEW_SECTION)}
                testId={CatalogProductsTestIds.MOVE_TO_SECTION_NEW_CHIP}
              />
            )}
          </ListItemButton>
        ))}

        <Box sx={{ paddingX: 0.75, paddingTop: 0.75 }}>
          <Stack
            direction="row"
            sx={(theme) => ({
              alignItems: 'center',
              minHeight: '32px',
              paddingRight: 0.25,
              paddingLeft: `${NAME_COLUMN_OFFSET_PX}px`,
              borderRadius: `${theme.shape.borderRadiusMd}px`,
              backgroundColor: theme.palette.action.selected,
              boxShadow: isNameDuplicated
                ? `inset 0 0 0 1px ${theme.palette.error.main}`
                : undefined,
            })}
          >
            <BaseInput
              value={draftInputValue}
              error={isNameDuplicated}
              ariaDescribedBy={isNameDuplicated ? DUPLICATED_NAME_ERROR_ID : undefined}
              placeholder={translate(LangEnum.CATALOGS_MOVE_TO_SECTION_MODAL_CREATE_PLACEHOLDER)}
              ariaLabel={translate(LangEnum.CATALOGS_MOVE_TO_SECTION_MODAL_CREATE_PLACEHOLDER)}
              testId={CatalogProductsTestIds.MOVE_TO_SECTION_CREATE_INPUT}
              sx={(theme) => ({ flex: 1, minWidth: 0, ...theme.typography.body1 })}
              onChange={(event) => handleChangeDraftInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  handleCreateDraftSection();
                }
              }}
            />
            <IconButton
              icon={Plus}
              size="medium"
              disabled={isCreateDisabled}
              ariaLabel={translate(LangEnum.CATALOGS_SECTIONS_CREATE_CONFIRM)}
              testId={CatalogProductsTestIds.MOVE_TO_SECTION_CREATE_CONFIRM}
              onClick={handleCreateDraftSection}
            />
          </Stack>
          {isNameDuplicated && (
            <Typography
              id={DUPLICATED_NAME_ERROR_ID}
              role="alert"
              variant="micro1"
              color="error.main"
              sx={{ paddingTop: 0.25 }}
            >
              {translate(LangEnum.CATALOGS_MOVE_TO_SECTION_MODAL_DUPLICATED_NAME, {
                name: typedSectionName,
              })}
            </Typography>
          )}
        </Box>
      </Stack>
    </Modal>
  );
};

export default CatalogMoveToSectionModal;
